import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { baseUrl } from '../../../app/api/api';
import { supportedLanguages } from '../../../app/utils/languageHelper';
import { useAppSelector } from '../../../app/store/configureStore';
import authService from '../../../app/services/authService';

interface ReceiptViewerProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  paymentId?: string;
  refundId?: string;
  isRefund?: boolean;
  isConsolidated?: boolean;
  printerType?: PrinterType;
}

export type PrinterType = 'A4' | 'Thermal';

const ReceiptViewer: React.FC<ReceiptViewerProps> = ({
  open,
  onClose,
  orderId,
  paymentId,
  refundId,
  isRefund = false,
  isConsolidated = false,
  printerType: propPrinterType = 'A4',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [printerType, setPrinterType] = useState<PrinterType>(propPrinterType);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const { selectedLanguage: defaultLanguage } = useAppSelector(
    (state) => state.language
  );

  useEffect(() => {
    setSelectedLanguage(defaultLanguage.culture);
  }, [defaultLanguage]);

  useEffect(() => {
    if (!open) return;

    const fetchReceipt = async () => {
      setLoading(true);
      setError(null);
      setPdfUrl(null);

      try {
        let url = '';
        const printerTypeParam = `?printerType=${printerType}`;
        const languageParam = `&language=${selectedLanguage}`;

        if (isConsolidated) {
          url = `receipts/order/${orderId}/consolidated${printerTypeParam}${languageParam}`;
        } else if (isRefund) {
          url = `receipts/order/${orderId}/refund${refundId ? `/${refundId}` : ''}${printerTypeParam}${languageParam}`;
        } else {
          url = `receipts/order/${orderId}/payment${paymentId ? `/${paymentId}` : ''}${printerTypeParam}${languageParam}`;
        }

        const token = authService.getAccessToken();
        if (!token) {
          throw new Error('No authentication token found.');
        }

        const response = await fetch(baseUrl + url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch receipt: ${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load receipt');
      } finally {
        setLoading(false);
      }
    };

    fetchReceipt();

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [
    open,
    orderId,
    paymentId,
    refundId,
    isRefund,
    isConsolidated,
    printerType,
    selectedLanguage,
  ]);

  useEffect(() => {
    setPrinterType(propPrinterType);
  }, [propPrinterType]);

  const handlePrint = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = function () {
          printWindow.print();
        };
      }
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      if (isConsolidated) {
        link.download = `Consolidated_Receipt_${orderId}.pdf`;
      } else {
        link.download = isRefund
          ? `Refund_Receipt_${orderId}_${refundId || 'latest'}.pdf`
          : `Payment_Receipt_${orderId}_${paymentId || 'latest'}.pdf`;
      }
      link.click();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="body"
    >
      <DialogTitle>
        {isConsolidated
          ? t('biller.consolidated-receipt')
          : isRefund
            ? t('biller.refund-receipt')
            : t('biller.payment-receipt')}
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        <Box sx={{ m: 1 }}>
          <Grid container spacing={isMobile ? 1 : 2}>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth variant="outlined">
                <InputLabel shrink>{t('biller.printer-type')}</InputLabel>
                <Select
                  value={printerType}
                  label={t('biller.printer-type')}
                  onChange={(e) =>
                    setPrinterType(e.target.value as PrinterType)
                  }
                >
                  <MenuItem value="A4">A4</MenuItem>
                  <MenuItem value="Thermal">Thermal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth variant="outlined">
                <InputLabel>{t('biller.select-receipt-language')}</InputLabel>
                <Select
                  value={selectedLanguage}
                  label={t('common.language')}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                >
                  {Array.from(supportedLanguages.entries()).map(
                    ([key, value]) => (
                      <MenuItem key={key} value={value.culture}>
                        {`${value.flag} ${value.language}`}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {pdfUrl && (
          <>
            <Box
              sx={{
                height: isMobile ? 400 : '70vh',
                width: '100%',
                background: '#f5f5f5',
              }}
            >
              <iframe
                src={pdfUrl}
                width="100%"
                height={isMobile ? 400 : '100%'}
                style={{ border: '1px solid #bbb', background: '#fff' }}
                title="Receipt PDF"
                onError={(e) => {
                  const iframe = e.target as HTMLIFrameElement;
                  iframe.style.display = 'none';
                  const fallback = document.getElementById('pdf-fallback');
                  if (fallback) fallback.style.display = 'block';
                }}
              />
            </Box>
            <Box
              id="pdf-fallback"
              sx={{ display: 'none', textAlign: 'center', my: 2 }}
            >
              <Typography color="error" sx={{ mb: 1 }}>
                {t('biller.pdf-not-supported-mobile') ||
                  'PDF preview is not supported on your device. Please download to view.'}
              </Typography>
              <Button onClick={handleDownload}>{t('common.download')}</Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
        <Button onClick={handleDownload}>{t('common.download')}</Button>
        <Button onClick={handlePrint}>{t('common.print')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptViewer;
